import { Component, OnInit, OnDestroy, ViewChild, Input, Output, EventEmitter, ElementRef, AfterViewInit, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subject, takeUntil, timer } from 'rxjs';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { AuthService } from 'app/core/auth/auth.service';
import { ActivityTimerComponent } from 'app/shared/activity-timer/activity-timer.component';
import { TwoFactorService } from '../../sign-in/sign-in-twofa/two-factor.service';

@Component({
    selector: 'twofa-form',
    templateUrl: './twofa-form.component.html',
    styleUrls: ['./twofa-form.component.scss'],
    imports: [
        ActivityTimerComponent,
        FuseAlertComponent,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule
    ],
})
export class TwofaFormComponent implements OnInit, OnDestroy, AfterViewInit
{
    private readonly _formBuilder = inject(UntypedFormBuilder);
    private readonly _authService = inject(AuthService);
    private readonly _twoFactorService = inject(TwoFactorService);
    private readonly _router = inject(Router);
    private readonly _route = inject(ActivatedRoute);

    @ViewChild('signInTwoFactorNgForm') signInTwoFactorNgForm: NgForm;
    @ViewChild('twoFactorCode') inputEl: ElementRef<HTMLInputElement>;
    @Input() twoFactorActivationRequired: boolean;
    @Output() successEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() errorEvent: EventEmitter<any> = new EventEmitter<any>();

    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };

    signInTwoFactorForm: UntypedFormGroup;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _errorMessage: string = 'Please enter a valid Authentication Code';
    private _errorAttemptsRemaining: number = 3;

    activityWatchedValue: string = '+';

    constructor()
    { }

    /**
     * On init
     */
    ngOnInit(): void {
        this.signInTwoFactorForm = this._formBuilder.group({
            twoFactorCode: ['', []],
        });

        // this.signInTwoFactorForm.valueChanges
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe({
        //         next: (changes) => {
        //             this.activityWatchedValue = '+' + (this.signInTwoFactorForm.get('twoFactorCode')?.value ?? '');
        //         }
        //     });

        // Redirect after 120s - Now Handled by "activity-timer" component
        // timer(120000)
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe({
        //         next: () => {
        //             this.errorEvent.emit();
        //         },
        //         complete: () => {},
        //     });
    }

    ngAfterViewInit(): void {
        timer(100)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this.inputEl.nativeElement.focus();
                },
                complete: () => {},
            });
    }

    /**
     * verify two factor auth
     */
    verifyTwoFactorCode(): void {
        const code = this.signInTwoFactorForm.get('twoFactorCode')?.value;
        if (!this._twoFactorService.isValidTwoFactorCode(code)) {
            this.failedVerifyCode(this._errorMessage)
            return;
        }

        if (this.twoFactorActivationRequired) {
            this._authService
            .enableTwoFactorAuthCode(code)
            .subscribe({
                next: (response) => {
                    this.successCallBack(response);
                },
                error: () => {
                    this.failedVerifyCode(this._errorMessage)
                },
            });
            return;
        }

        this._authService
            .verifyTwoFactorCode(code)
            .subscribe({
                next: (response) => {
                    this.successCallBack(response);
                },
                error: () => {
                    this.failedVerifyCode(this._errorMessage)
                },
            });
    }

    successCallBack(response: any) {
        if(!response.success) {
            return;
        }

        this.successEvent.emit(response);
    }

    private failedVerifyCode(message: string) {
        this.signInTwoFactorNgForm.resetForm();
        this._errorAttemptsRemaining --;
        if(!this._errorAttemptsRemaining) {
            this.errorEvent.emit();
            return;
        }

        this.alert = {
            type: 'error',
            message: message,
        };
    }


    onActivityTimedOut(): void {
        // Reset forms due to inactivity
        try { this.signInTwoFactorNgForm?.resetForm(); } catch (e) { /* ignore */ }
        try { this.signInTwoFactorForm?.reset(); } catch (e) { /* ignore */ }
        this.errorEvent.emit();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
